import { isPlatformServer } from '@angular/common';
import {
  Inject,
  Injectable,
  PLATFORM_ID,
  TransferState,
  makeStateKey,
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { CMS_CONTEXT, CmsContainer, CmsService } from '@domgen/dgx-fe-cms';
import { NavigationLink, filterNullUndefined } from '@domgen/dgx-fe-common';
import { Subnavigation } from '@domgen/dgx-fe-components/layout';
import { VideoBlock } from '@domgen/dgx-fe-components/media';
import {
  ApplianceDetailsFormField,
  CmsArticleSearchPage,
  CmsYourAppliancePage,
  PopularVideo as PVModel,
  YourAppliance,
} from '@self-fix/util-models';
import {
  Observable,
  combineLatest,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';

export const cmsArticleSearchResponseKey = makeStateKey<{
  cmsArticleSearchResponse: string;
}>('cmsArticleSearchResponse');

interface ViewModel {
  selectedApplianceCode: string | undefined;
  popularArticlesTitle: string;
  popularArticles: NavigationLink[];
  popularVideosTitle: string;
  popularVideosBody: string;
  popularVideos: VideoBlock[];
  articleCategoriesTitle: string;
  articleCategoriesBody: string;
  viewAllArticlesLink: NavigationLink;
  viewAllVideosLink: NavigationLink;
  articleCategories: string[];
  categorisedArticlesAndVideos: Subnavigation[];
  repairRedirectTitle: string;
  repairRedirectBody: string;
  repairRedirectIconName: string;
  repairRedirectPrimaryLink: NavigationLink;
  repairRedirectSecondaryLink: NavigationLink;
  cmsData: CmsYourAppliancePage;
}

interface PopularVideo extends PVModel {
  videoId?: string;
}

@Injectable()
export class YourApplianceComponentService {
  constructor(
    @Inject(CMS_CONTEXT)
    private readonly cmsContext: EditorContextHelper.IMagnoliaContext,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly cmsContainer: CmsContainer<CmsYourAppliancePage>,
    private readonly cmsService: CmsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly transferState: TransferState
  ) {}

  readonly selectedApplianceCode$: Observable<string | undefined> =
    this.activatedRoute.queryParamMap.pipe(
      map((params: ParamMap) =>
        this.cmsContext.isMagnoliaEdit || this.cmsContext.isMagnoliaPreview
          ? this.cmsContainer.content.appliances[0].applianceCode
          : params.get(ApplianceDetailsFormField.ApplianceCode)
      ),
      filterNullUndefined()
    );

  readonly selectedApplianceCmsContent$: Observable<YourAppliance | undefined> =
    combineLatest([
      this.cmsContainer.content$,
      this.selectedApplianceCode$,
    ]).pipe(
      map(([config, selectedApplianceCode]) =>
        config.appliances.find(
          (appliance) => appliance.applianceCode === selectedApplianceCode
        )
      ),
      mergeMap((appliance: YourAppliance | undefined) => {
        if (appliance && appliance.popularVideosBlock?.popularVideos) {
          const videos = Object.values(
            appliance.popularVideosBlock?.popularVideos
          );
          appliance.popularVideosBlock.popularVideos = videos.filter(
            (v) => v.popularVideo
          );
        }
        if (appliance && appliance.popularArticlesBlock?.popularArticles) {
          const articles = Object.values(
            appliance.popularArticlesBlock?.popularArticles
          );
          appliance.popularArticlesBlock.popularArticles = articles.filter(
            (a) => a.popularArticle
          );
        }
        return of(appliance);
      })
    );

  readonly popularArticlesTitle$: Observable<string> =
    this.selectedApplianceCmsContent$.pipe(
      map(
        (appliance) =>
          appliance?.popularArticlesBlock?.popularArticlesTitle ?? ''
      )
    );

  readonly popularArticles$: Observable<NavigationLink[]> =
    this.selectedApplianceCmsContent$.pipe(
      map((appliance) =>
        appliance?.popularArticlesBlock
          ? appliance.popularArticlesBlock.popularArticles.map((article) => {
              return {
                href: article.popularArticle?.['@path']?.replace(
                  /\/([A-Za-z-]+\/){3}/,
                  ''
                ),
                label: article.popularArticle.articleTitle,
              };
            })
          : []
      )
    );

  readonly popularVideosTitle$: Observable<string> =
    this.selectedApplianceCmsContent$.pipe(
      map((appliance) => appliance?.popularVideosBlock?.popularVideosBody ?? '')
    );

  readonly popularVideosBody$: Observable<string> =
    this.selectedApplianceCmsContent$.pipe(
      map(
        (appliance) => appliance?.popularVideosBlock?.popularVideosTitle ?? ''
      )
    );

  readonly viewAllVideosLink$: Observable<NavigationLink> =
    this.selectedApplianceCmsContent$.pipe(
      map((appliance) =>
        appliance?.popularVideosBlock
          ? ({
              href: appliance.popularVideosBlock.viewAllVideosLink.routerPath,
              label: appliance.popularVideosBlock.viewAllVideosLink.label,
            } as NavigationLink)
          : ({
              href: '',
              label: '',
            } as NavigationLink)
      )
    );

  readonly popularVideos$: Observable<VideoBlock[]> =
    this.selectedApplianceCmsContent$.pipe(
      map((appliance) =>
        appliance?.popularVideosBlock
          ? appliance.popularVideosBlock.popularVideos.map((video) => {
              return {
                title: video.popularVideo.articleTitle,
                body: video.popularVideo.introText || '',
                videoId: (video.popularVideo as PopularVideo).videoId || '',
                navigationLink: {
                  href: video.popularVideo['@path'].replace(
                    /\/([A-Za-z-]+\/){3}/,
                    ''
                  ),
                  label: video.popularVideo.articleTitle,
                },
              };
            })
          : []
      )
    );

  readonly articleCategoriesTitle$: Observable<string> =
    this.cmsContainer.content$.pipe(
      map((content) => content.articleCategoriesTitle)
    );

  readonly articleCategoriesBody$: Observable<string> =
    this.cmsContainer.content$.pipe(
      map((content) => content.articleCategoriesBody)
    );

  readonly viewAllArticlesLink$: Observable<NavigationLink> =
    this.cmsContainer.content$.pipe(
      map(
        (content) =>
          ({
            href: content?.articleCategoriesViewMoreLink?.routerPath,
            label: content?.articleCategoriesViewMoreLink?.label,
          }) as NavigationLink
      )
    );

  readonly articleCategories$: Observable<string[]> =
    this.selectedApplianceCmsContent$.pipe(
      map((appliance) =>
        appliance?.articleCategories
          ? appliance.articleCategories.map((category) =>
              category.articleCategory['@path'].replace(
                '/gb/domestic-and-general/self-fix/',
                ''
              )
            )
          : []
      )
    );

  readonly categorisedArticlesAndVideos$: Observable<Subnavigation[]> =
    this.articleCategories$.pipe(
      switchMap((categories) =>
        this.getCategorisedArticlesAndVideos(categories)
      )
    );

  readonly repairRedirectTitle$: Observable<string> =
    this.cmsContainer.content$.pipe(
      map((content) => content.repairRedirectTitle)
    );

  readonly repairRedirectBody$: Observable<string> =
    this.cmsContainer.content$.pipe(
      map((content) => content.repairRedirectBody)
    );

  readonly repairRedirectIconName$: Observable<string> =
    this.cmsContainer.content$.pipe(
      map((content) => content.repairRedirectIconName)
    );

  readonly repairRedirectPrimaryLink$: Observable<NavigationLink> =
    this.cmsContainer.content$.pipe(
      map((content) => content.repairRedirectPrimaryLink)
    );

  readonly repairRedirectSecondaryLink$: Observable<NavigationLink> =
    this.cmsContainer.content$.pipe(
      map((content) => content.repairRedirectSecondaryLink)
    );

  readonly vm$: Observable<ViewModel> = combineLatest({
    selectedApplianceCode: this.selectedApplianceCode$,
    popularArticlesTitle: this.popularArticlesTitle$,
    popularArticles: this.popularArticles$,
    popularVideosTitle: this.popularVideosTitle$,
    popularVideosBody: this.popularVideosBody$,
    popularVideos: this.popularVideos$,
    articleCategoriesTitle: this.articleCategoriesTitle$,
    articleCategoriesBody: this.articleCategoriesBody$,
    viewAllArticlesLink: this.viewAllArticlesLink$,
    viewAllVideosLink: this.viewAllVideosLink$,
    articleCategories: this.articleCategories$,
    categorisedArticlesAndVideos: this.categorisedArticlesAndVideos$,
    repairRedirectTitle: this.repairRedirectTitle$,
    repairRedirectBody: this.repairRedirectBody$,
    repairRedirectIconName: this.repairRedirectIconName$,
    repairRedirectPrimaryLink: this.repairRedirectPrimaryLink$,
    repairRedirectSecondaryLink: this.repairRedirectSecondaryLink$,
    cmsData: this.cmsContainer.content$,
  });

  getCategorisedArticlesAndVideos(
    categories: string[]
  ): Observable<Subnavigation[]> {
    return forkJoin(
      categories.map((category) =>
        this.cmsService
          .search<CmsArticleSearchPage>({
            orderBy: 'articleDate desc',
            'mgnl:template': 'dgx-self-fix:pages/self-fix-article',
            limit: 50,
            path: category,
          })
          .pipe(
            map(
              (accordionArticles) =>
                ({
                  title: category
                    .substring(category.lastIndexOf('/') + 1)
                    .replace(/-/g, ' '),
                  links: accordionArticles.results.map((result) => {
                    return {
                      href: result['@path'].replace(/\/([A-Za-z-]+\/){3}/, ''),
                      label: result.articleTitle,
                    };
                  }),
                }) as Subnavigation
            )
          )
      )
    ).pipe(
      switchMap((cmsArticleSearchResponse: Subnavigation[]) => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(cmsArticleSearchResponseKey, {
            cmsArticleSearchResponse: JSON.stringify(cmsArticleSearchResponse),
          });
        }

        return of(
          JSON.parse(
            this.transferState.get<{
              cmsArticleSearchResponse: string;
            }>(cmsArticleSearchResponseKey, {
              cmsArticleSearchResponse: JSON.stringify(
                cmsArticleSearchResponse
              ),
            }).cmsArticleSearchResponse
          )
        );
      })
    );
  }
}
